import React from 'react';
import { motion } from 'framer-motion';
import { Target, Brain, Scale, BarChart3, Award, Users, Sparkles, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';

const AboutPage = () => {
  const navigate = useNavigate();

  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const pillarItems = [
    '5 piliers du management moderne',
    '20 modules de formation structurés',
    '2 objectifs pédagogiques clairs par module',
    'Des cours théoriques accessibles en lecture ou en audio',
    'Des entraînements écrits et vocaux en situations managériales réalistes',
    'Une évaluation fondée sur des preuves observables'
  ];

  const owenCapabilities = [
    'met l\'apprenant en situation réelle,',
    'observe les comportements,',
    'exige des preuves concrètes,',
    'challenge les réponses floues,',
    'et rend une décision pédagogique explicite.'
  ];

  const progressionLevels = [
    { level: '40%', label: 'compétence en émergence', color: '#FBBF24' },
    { level: '60%', label: 'compétence en consolidation', color: '#F97316' },
    { level: '100%', label: 'compétence validée', color: '#10B981' }
  ];

  const tracabilityFeatures = [
    'le suivi des objectifs pédagogiques,',
    'la conservation des preuves d\'acquisition,',
    'l\'historisation des décisions pédagogiques,',
    'l\'export des données pour audit ou certification.'
  ];

  const qualiopiFeatures = [
    'information claire avant l\'entrée en formation,',
    'objectifs pédagogiques explicites,',
    'méthodes pédagogiques identifiées,',
    'évaluations fondées sur des preuves,',
    'suivi et amélioration continue.'
  ];

  const targetAudience = [
    'managers en poste,',
    'nouveaux managers,',
    'chefs de projet,',
    'responsables d\'équipe,',
    'professionnels en évolution de rôle.'
  ];

  const summaryPoints = [
    'une formation manageriale augmentee par l\'IA,',
    'des entrainements realistes,',
    'une evaluation exigeante,',
    'une progression mesurable,',
    'un Espace Coachs certifies (video et presentiel),',
    'des rapports IA post-session,',
    'et une transformation durable.'
  ];

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FEF9E7 0%, #F5F3FF 50%, #FDF2F8 100%)' }}>
      <Navbar />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Back Button */}
          <motion.button
            {...fadeInUp}
            onClick={() => navigate('/')}
            className="flex items-center gap-2 mb-8 text-[#3D2B73] hover:text-[#F59E0B] transition-colors font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Retour à l&apos;accueil
          </motion.button>

          {/* Main Title */}
          <motion.div {...fadeInUp} className="text-center mb-12">
            <h1 
              className="text-4xl md:text-5xl lg:text-6xl font-black mb-6"
              style={{ 
                fontFamily: "'Manrope', sans-serif",
                color: '#1C104A',
                letterSpacing: '-0.02em'
              }}
            >
              À propos de{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] via-[#F472B6] to-[#8B5CF6]">
                Coach DIA
              </span>
            </h1>
            <p className="text-xl md:text-2xl" style={{ color: '#3D2B73', fontWeight: 500, lineHeight: 1.6 }}>
              La formation managériale augmentée par l&apos;IA, évaluée sur la pratique
            </p>
          </motion.div>

          {/* Introduction */}
          <motion.div {...fadeInUp} className="prose prose-lg max-w-none mb-12">
            <p className="text-lg" style={{ color: '#3D2B73', lineHeight: 1.8 }}>
              <strong>Coach DIA</strong> est une plateforme de formation professionnelle dédiée au management moderne, 
              conçue pour les managers et futurs managers qui veulent développer des <strong>compétences concrètes, 
              observables et durables</strong>, dans un monde où la complexité humaine et technologique s&apos;accélère.
            </p>
            <p className="text-lg mt-4" style={{ color: '#4B3869', lineHeight: 1.8 }}>
              Ici, il ne s&apos;agit pas de consommer des contenus passivement.<br />
              <strong>Il s&apos;agit de se transformer par la pratique, avec exigence, clarté et traçabilité.</strong>
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section: Une conviction forte */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ background: 'rgba(255, 255, 255, 0.5)' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp} className="flex items-start gap-4 mb-8">
            <div className="p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, #F59E0B, #FBBF24)' }}>
              <Target className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1C104A' }}>
              Une conviction forte
            </h2>
          </motion.div>
          
          <motion.blockquote 
            {...fadeInUp}
            className="border-l-4 pl-6 py-4 mb-8"
            style={{ borderColor: '#F59E0B', background: 'rgba(245, 158, 11, 0.1)', borderRadius: '0 12px 12px 0' }}
          >
            <p className="text-lg italic" style={{ color: '#1C104A', fontWeight: 500 }}>
              On ne devient pas un bon manager en apprenant des concepts.<br />
              On le devient en s&apos;entraînant sur des situations réelles, en étant évalué sur ce que l&apos;on fait réellement.
            </p>
          </motion.blockquote>

          <motion.p {...fadeInUp} className="text-lg" style={{ color: '#3D2B73', lineHeight: 1.8 }}>
            <strong>Coach DIA</strong> est né de ce constat :<br />
            les formations managériales traditionnelles expliquent beaucoup… <strong>mais mesurent peu</strong>.
          </motion.p>
        </div>
      </section>

      {/* Section: Approche pédagogique */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp} className="flex items-start gap-4 mb-8">
            <div className="p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, #8B5CF6, #A78BFA)' }}>
              <Brain className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1C104A' }}>
              Une approche pédagogique unique
            </h2>
          </motion.div>

          <motion.p {...fadeInUp} className="text-lg mb-6" style={{ color: '#3D2B73', lineHeight: 1.8 }}>
            Coach DIA repose sur une <strong>pédagogie expérientielle, progressive et évaluative</strong>, articulée autour de :
          </motion.p>

          <motion.ul {...fadeInUp} className="space-y-3 mb-8">
            {pillarItems.map((item, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-[#F59E0B] mt-1">•</span>
                <span className="text-lg" style={{ color: '#3D2B73' }}>{item}</span>
              </li>
            ))}
          </motion.ul>

          <motion.div {...fadeInUp} className="p-6 rounded-2xl" style={{ background: 'rgba(139, 92, 246, 0.1)' }}>
            <p className="text-lg font-medium mb-4" style={{ color: '#1C104A' }}>
              Chaque module suit une logique simple et exigeante :
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              {[
                '1. Comprendre (cours théorique)',
                '2. S\'entraîner (écrit + vocal)',
                '3. Être évalué sur la pratique réelle',
                '4. Progresser ou consolider, preuves à l\'appui'
              ].map((step, index) => (
                <div key={index} className="flex items-center gap-2 p-3 rounded-xl bg-white/50">
                  <span className="text-lg" style={{ color: '#3D2B73', fontWeight: 500 }}>{step}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Section: Owen */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ background: 'rgba(255, 255, 255, 0.5)' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp} className="flex items-start gap-4 mb-8">
            <div className="p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, #F472B6, #EC4899)' }}>
              <Scale className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1C104A' }}>
              Owen : une IA pédagogique, pas un chatbot
            </h2>
          </motion.div>

          <motion.p {...fadeInUp} className="text-lg mb-6" style={{ color: '#3D2B73', lineHeight: 1.8 }}>
            Au cœur de Coach DIA se trouve <strong>Owen</strong>, un coach IA pédagogique conçu comme une 
            <strong> autorité évaluative</strong>, et non comme un simple assistant conversationnel.
          </motion.p>

          <motion.div {...fadeInUp} className="mb-8">
            <p className="text-lg font-medium mb-4" style={{ color: '#1C104A' }}>Owen :</p>
            <ul className="space-y-2">
              {owenCapabilities.map((capability, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-[#EC4899] mt-1">•</span>
                  <span className="text-lg" style={{ color: '#3D2B73' }}>{capability}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fadeInUp} className="p-6 rounded-2xl mb-6" style={{ background: 'rgba(236, 72, 153, 0.1)' }}>
            <p className="text-lg font-medium mb-4" style={{ color: '#1C104A' }}>
              Les niveaux de progression sont clairs et assumés :
            </p>
            <div className="space-y-3">
              {progressionLevels.map((item, index) => (
                <div key={index} className="flex items-center gap-4">
                  <span 
                    className="text-2xl font-bold px-4 py-2 rounded-lg"
                    style={{ background: item.color, color: 'white' }}
                  >
                    {item.level}
                  </span>
                  <span className="text-lg" style={{ color: '#3D2B73' }}>{item.label}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div {...fadeInUp} className="p-4 rounded-xl border-2 border-dashed" style={{ borderColor: '#EC4899' }}>
            <p className="text-lg font-semibold" style={{ color: '#1C104A' }}>
              👉 Aucune progression n&apos;est validée sans preuve observable.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section: Traçabilité */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp} className="flex items-start gap-4 mb-8">
            <div className="p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, #10B981, #34D399)' }}>
              <BarChart3 className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1C104A' }}>
              Une progression traçable et opposable
            </h2>
          </motion.div>

          <motion.p {...fadeInUp} className="text-lg mb-6" style={{ color: '#3D2B73', lineHeight: 1.8 }}>
            Coach DIA a été conçu dès l&apos;origine pour répondre aux <strong>exigences de qualité de la formation professionnelle</strong>.
          </motion.p>

          <motion.div {...fadeInUp} className="mb-8">
            <p className="text-lg font-medium mb-4" style={{ color: '#1C104A' }}>Chaque parcours permet :</p>
            <ul className="space-y-2">
              {tracabilityFeatures.map((feature, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-[#10B981] mt-1">•</span>
                  <span className="text-lg" style={{ color: '#3D2B73' }}>{feature}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div {...fadeInUp} className="p-6 rounded-2xl" style={{ background: 'rgba(16, 185, 129, 0.1)' }}>
            <p className="text-lg font-medium mb-3" style={{ color: '#1C104A' }}>
              À chaque module validé :
            </p>
            <ul className="space-y-2">
              <li className="flex items-start gap-3">
                <span className="text-[#10B981] mt-1">•</span>
                <span className="text-lg" style={{ color: '#3D2B73' }}>une <strong>attestation de formation</strong> est générée,</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-[#10B981] mt-1">•</span>
                <span className="text-lg" style={{ color: '#3D2B73' }}>l&apos;<strong>attestation multi-modules</strong> est automatiquement mise à jour.</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </section>

      {/* Section: Qualiopi & CPF */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ background: 'rgba(255, 255, 255, 0.5)' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp} className="flex items-start gap-4 mb-8">
            <div className="p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, #F59E0B, #FBBF24)' }}>
              <Award className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1C104A' }}>
              Une plateforme pensée pour la conformité Qualiopi & CPF
            </h2>
          </motion.div>

          <motion.p {...fadeInUp} className="text-lg mb-6" style={{ color: '#3D2B73', lineHeight: 1.8 }}>
            Coach DIA s&apos;inscrit dans une logique de <strong>formation certifiante et finançable</strong>, avec :
          </motion.p>

          <motion.ul {...fadeInUp} className="space-y-2 mb-8">
            {qualiopiFeatures.map((feature, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-[#F59E0B] mt-1">•</span>
                <span className="text-lg" style={{ color: '#3D2B73' }}>{feature}</span>
              </li>
            ))}
          </motion.ul>

          <motion.div {...fadeInUp} className="p-4 rounded-xl" style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(251, 191, 36, 0.1))' }}>
            <p className="text-lg font-semibold" style={{ color: '#1C104A' }}>
              🏅 La plateforme est conçue pour être <strong>audit-ready</strong>, sans bricolage a posteriori.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Section: À qui s'adresse */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp} className="flex items-start gap-4 mb-8">
            <div className="p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, #6366F1, #818CF8)' }}>
              <Users className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1C104A' }}>
              À qui s&apos;adresse Coach DIA ?
            </h2>
          </motion.div>

          <motion.p {...fadeInUp} className="text-lg mb-6" style={{ color: '#3D2B73', lineHeight: 1.8 }}>
            Coach DIA s&apos;adresse à toute personne souhaitant développer une <strong>posture managériale solide et moderne</strong> :
          </motion.p>

          <motion.ul {...fadeInUp} className="space-y-2 mb-8">
            {targetAudience.map((audience, index) => (
              <li key={index} className="flex items-start gap-3">
                <span className="text-[#6366F1] mt-1">•</span>
                <span className="text-lg" style={{ color: '#3D2B73' }}>{audience}</span>
              </li>
            ))}
          </motion.ul>

          <motion.p {...fadeInUp} className="text-lg" style={{ color: '#3D2B73', lineHeight: 1.8 }}>
            Aucun prérequis technique n&apos;est nécessaire.<br />
            <strong>Seule compte la volonté de progresser par l&apos;action.</strong>
          </motion.p>
        </div>
      </section>

      {/* Section: Résumé */}
      <section className="py-16 px-4 sm:px-6 lg:px-8" style={{ background: 'rgba(255, 255, 255, 0.5)' }}>
        <div className="max-w-4xl mx-auto">
          <motion.div {...fadeInUp} className="flex items-start gap-4 mb-8">
            <div className="p-3 rounded-xl" style={{ background: 'linear-gradient(135deg, #F59E0B, #F472B6, #8B5CF6)' }}>
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#1C104A' }}>
              Coach DIA, en résumé
            </h2>
          </motion.div>

          <motion.div {...fadeInUp} className="mb-8">
            <p className="text-lg font-medium mb-4" style={{ color: '#1C104A' }}>Coach DIA, c&apos;est :</p>
            <ul className="space-y-2">
              {summaryPoints.map((point, index) => (
                <li key={index} className="flex items-start gap-3">
                  <span className="text-[#F59E0B] mt-1">•</span>
                  <span className="text-lg" style={{ color: '#3D2B73' }}>{point}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div 
            {...fadeInUp}
            className="p-8 rounded-2xl text-center"
            style={{ background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.2), rgba(244, 114, 182, 0.2), rgba(139, 92, 246, 0.2))' }}
          >
            <p className="text-xl font-semibold mb-2" style={{ color: '#1C104A' }}>
              Ici, vous ne &quot;suivez&quot; pas une formation.
            </p>
            <p className="text-2xl font-bold" style={{ color: '#1C104A' }}>
              Vous <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] via-[#F472B6] to-[#8B5CF6]">prouvez</span> que vous progressez.
            </p>
          </motion.div>

          {/* CTA */}
          <motion.div {...fadeInUp} className="text-center mt-12">
            <button
              onClick={() => navigate('/#plans')}
              className="px-8 py-4 rounded-xl text-lg font-bold text-white transition-all hover:scale-105"
              style={{ background: 'linear-gradient(135deg, #F59E0B, #F472B6)' }}
            >
              Découvrir nos formules
            </button>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
